/**
 * DrawdownBar — ASC 606 Phase 3c
 * Visual progress bar for MSA draw-down (consumed / committed).
 *
 * Color tiers driven by the backend `warning` string:
 *   null                  -> green  (healthy, <80% used)
 *   APPROACHING_CEILING   -> amber  (80-94%)
 *   NEAR_CEILING          -> orange (95-99%)
 *   EXCEEDED              -> red    (100%+)
 *
 * Visual fill is clamped to 100% even when overage; an overage badge is
 * shown separately so the user sees BOTH the bar-full-at-100% and the
 * literal "112% used" number.
 */
import React from 'react';

type WarningTier = null | 'APPROACHING_CEILING' | 'NEAR_CEILING' | 'EXCEEDED';

interface DrawdownBarProps {
  pctUsed: number;             // 0-100+, e.g. 42.5 or 112.0
  warning?: WarningTier | string | null;
  showLabel?: boolean;         // show the "X.X%" text on the right (default true)
  height?: number;              // px, default 10
}

function colorsFor(warning: WarningTier | string | null | undefined): { fill: string; label: string; bg: string } {
  switch (warning) {
    case 'EXCEEDED':
      return {
        fill: 'var(--negative, #ef4444)',
        label: 'var(--negative, #ef4444)',
        bg: 'rgba(239, 68, 68, 0.12)',
      };
    case 'NEAR_CEILING':
      return {
        fill: '#fb923c', // orange-400
        label: '#fb923c',
        bg: 'rgba(251, 146, 60, 0.12)',
      };
    case 'APPROACHING_CEILING':
      return {
        fill: 'var(--warning, #f59e0b)',
        label: 'var(--warning, #f59e0b)',
        bg: 'rgba(245, 158, 11, 0.12)',
      };
    default:
      return {
        fill: 'var(--positive, #10b981)',
        label: 'var(--positive, #10b981)',
        bg: 'rgba(16, 185, 129, 0.12)',
      };
  }
}

export default function DrawdownBar({
  pctUsed,
  warning = null,
  showLabel = true,
  height = 10,
}: DrawdownBarProps) {
  const colors = colorsFor(warning);
  const safePct = Number.isFinite(pctUsed) ? Math.max(0, pctUsed) : 0;
  const visualPct = Math.min(100, safePct);
  const isOverage = safePct > 100;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
      <div
        style={{
          flex: 1,
          height,
          background: 'var(--border-subtle, #e5e7eb)',
          borderRadius: height / 2,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          style={{
            width: `${visualPct}%`,
            height: '100%',
            background: colors.fill,
            borderRadius: height / 2,
            transition: 'width 0.3s ease',
          }}
        />
      </div>
      {showLabel && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 80 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: colors.label, minWidth: 50, textAlign: 'right' }}>
            {safePct.toFixed(1)}%
          </span>
          {isOverage && (
            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                padding: '2px 6px',
                borderRadius: 4,
                background: colors.bg,
                color: colors.label,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              OVER
            </span>
          )}
        </div>
      )}
    </div>
  );
}
